import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "@/lib/cart";
import { inr } from "@/lib/content";
import { track } from "@/lib/metaPixel";
import { gTrack } from "@/lib/ga";
import { LogoMark } from "./Logo";

const HOVERABLE =
  typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches;

export default function ProductCard({ product, index, dark = false, aspect = "aspect-square", fill = false, className = "" }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);  const { add, setOpen } = useCart();
  const main = dark ? "text-bone" : "text-ink";
  const sub = dark ? "text-bone/50" : "text-ink/55";
  const useVariants = product.sizes.length === 0;
  const options = useVariants ? product.variants : product.sizes;
  const soldOut = options.length > 0 && options.every((o) => o.stock === 0);

  const quickAdd = (o) => {
    if (o.stock === 0) return;
    const unit = o.price || product.price;
    add({
      slug: product.slug,
      name: product.name,
      colour: product.colour,
      price: unit,
      size: useVariants ? "" : o.label,
      variant: useVariants ? o.label : product.fabrics?.[0] || "",
      image: product.images[0] || "",
      qty: 1,
    });
    track("AddToCart", {
      content_name: product.name,
      content_type: "product",
      content_ids: [product.id],
      currency: "INR",
      value: unit,
    });
    gTrack("add_to_cart", {
      currency: "INR",
      value: unit,
      items: [{ item_id: product.slug, item_name: product.name, price: unit, quantity: 1 }],
    });
    setOpen(true);
  };

  return (
    <div
      className={`group relative ${className}`}
      onMouseEnter={HOVERABLE ? () => setHovered(true) : undefined}
      onMouseLeave={HOVERABLE ? () => setHovered(false) : undefined}
    >
      <span
        className="absolute left-3 top-3 z-10 border border-bone/20 bg-ink/90 px-2 py-1 font-mono text-[9px] leading-none tracking-[0.3em] text-bone"
        aria-hidden="true"
      >
        00{index + 1}
      </span>
      <Link
        to={`/product/${product.slug}`}
        data-testid={`product-card-${product.slug}`}
        aria-label={`${product.name} — ${product.colour}`}
        className={`relative block overflow-hidden bg-surface ${aspect}`}
      >
        {product.images.length > 0 ? (
          <>
            <img
              src={product.images[0]}
              alt={`${product.name} — ${product.colour}`}
              loading="lazy"
              decoding="async"
              className={`absolute inset-0 h-full w-full ${fill ? "object-cover" : "object-contain"} transition-transform duration-700 ease-out group-hover:scale-[1.02]`}
            />
            {HOVERABLE && hovered && product.images.length > 1 && (
              <motion.img
                key={product.images[1]}
                src={product.images[1]}
                alt=""
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className={`pointer-events-none absolute inset-0 h-full w-full ${fill ? "object-cover" : "object-contain"}`}
              />
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-surface">
            <LogoMark className="h-9 w-9 opacity-70" />
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-bone/50">IMAGE COMING SOON</span>
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 border border-black/10" aria-hidden="true" />
        <span
          className="pointer-events-none absolute bottom-0 left-0 h-0.5 w-0 bg-gold transition-all duration-500 group-hover:w-full"
          aria-hidden="true"
        />
      </Link>
      <div className="flex items-baseline justify-between gap-3 pt-3.5">
        <h3 className={`font-display text-xl leading-none tracking-[0.05em] ${main}`}>{product.name}</h3>
        <span className={`shrink-0 font-mono text-sm ${main}`}>
          {product.price == null ? "₹—" : inr(product.price)}
        </span>
      </div>
      <p className={`mt-1.5 font-mono text-[10px] uppercase tracking-[0.22em] ${sub}`}>
        {product.colour} · {product.category || "REQUIEM FEVER"}
      </p>
      {product.price == null ? (
        <p className={`mt-2.5 font-mono text-[10px] uppercase tracking-[0.25em] ${sub}`}>
          PRICE TBA — ENQUIRE ON INSTAGRAM
        </p>
      ) : (
        <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid={`card-sizes-${product.slug}`}>
          {options.map((o) => {
            const out = o.stock === 0;
            return (
              <button
                key={o.label}
                data-testid={`card-size-${product.slug}-${o.label}`}
                disabled={out}
                onClick={() => quickAdd(o)}
                aria-label={`Add ${o.label} to bag`}
                title={out ? "Sold out" : `Quick add — ${o.label}${o.price && o.price > product.price ? ` (+₹${o.price - product.price})` : ""}`}
                className={`h-9 min-w-11 border px-2 font-mono text-[11px] transition-colors ${
                  out
                    ? "cursor-not-allowed border-line/60 text-bone/25 line-through"
                    : dark
                    ? "border-line text-bone/75 hover:bg-bone hover:text-ink"
                    : "border-ink/20 text-ink/75 hover:bg-ink hover:text-bone"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

